import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ChevronRight, ChevronLeft, Check, Scan } from 'lucide-react';
import IDScannerModal from './IDScannerModal';

const MultiStepReservationModal = ({ open, onClose, initialData, onSubmit, property }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Stay & Rate
    check_in: initialData?.check_in || '',
    check_out: initialData?.check_out || '',
    unit_id: initialData?.unit_id || '',
    adults: 1,
    children: 0,
    base_amount: '',  // No prefilled amount
    tax_amount: 0,
    
    // Step 2: Guest
    guest_id: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_dob: '',
    guest_address: '',
    document_type: '',
    document_number: '',
    guest_note: '',
    
    // Step 3: Payment
    payment_method: 'Cash',
    payment_amount: 0,
    notes: ''
  });

  // Auto-calculate tax when base_amount changes
  useEffect(() => {
    if (property && formData.base_amount > 0) {
      const totalTaxRate = parseFloat(property.tax_total_rate) || 0;
      const calculatedTax = (formData.base_amount * totalTaxRate) / 100;
      
      setFormData(prev => ({
        ...prev,
        tax_amount: parseFloat(calculatedTax.toFixed(2))
      }));
    } else if (formData.base_amount <= 0 || !formData.base_amount) {
      // Reset tax if no base amount
      setFormData(prev => ({
        ...prev,
        tax_amount: 0
      }));
    }
  }, [formData.base_amount, property]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      // Reset to step 1 and clear form data
      setCurrentStep(1);
      setShowScannerModal(false);
      setFormData({
        check_in: initialData?.check_in || '',
        check_out: initialData?.check_out || '',
        unit_id: initialData?.unit_id || '',
        adults: 1,
        children: 0,
        base_amount: '',
        tax_amount: 0,
        guest_id: '',
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        guest_dob: '',
        guest_address: '',
        document_type: '',
        document_number: '',
        guest_note: '',
        payment_method: 'Cash',
        payment_amount: 0,
        notes: ''
      });
    }
  }, [open, initialData]);

  const steps = [
    { number: 1, title: 'Stay & Rate', desc: 'Dates and pricing' },
    { number: 2, title: 'Guest Details', desc: 'Contact and ID' },
    { number: 3, title: 'Payment', desc: 'Payment method' }
  ];

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const handleScannedData = (scannedData) => {
    if (!scannedData) return;
    
    // Auto-fill form with scanned data
    const updates = {};
    
    if (scannedData.fullName) {
      updates.guest_name = scannedData.fullName;
    } else if (scannedData.firstName || scannedData.lastName) {
      updates.guest_name = [scannedData.firstName, scannedData.lastName].filter(Boolean).join(' ');
    }
    
    if (scannedData.documentNumber) updates.document_number = scannedData.documentNumber;
    if (scannedData.documentType) updates.document_type = scannedData.documentType;
    if (scannedData.dateOfBirth) updates.guest_dob = scannedData.dateOfBirth;
    if (scannedData.addressLine1) {
      const addressParts = [
        scannedData.addressLine1,
        scannedData.city,
        scannedData.state,
        scannedData.postalCode
      ].filter(Boolean);
      updates.guest_address = addressParts.join(', ');
    }
    
    setFormData(prev => ({ ...prev, ...updates }));
    setShowScannerModal(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Reservation</DialogTitle>
        </DialogHeader>

        {/* Progress Indicator - Fixed alignment */}
        <div className="flex items-start justify-between mb-6">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  currentStep >= step.number 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {currentStep > step.number ? <Check size={20} /> : step.number}
                </div>
                <p className="text-xs mt-1 font-medium text-center">{step.title}</p>
                <p className="text-xs text-slate-500 text-center">{step.desc}</p>
              </div>
              {index < steps.length - 1 && (
                <div className={`h-1 mx-2 mt-5 flex-1 ${
                  currentStep > step.number ? 'bg-cyan-600' : 'bg-slate-200'
                }`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Stay & Rate */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Stay & Rate Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Check-In Date *</Label>
                  <Input
                    type="date"
                    value={formData.check_in}
                    onChange={(e) => setFormData({...formData, check_in: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Check-Out Date *</Label>
                  <Input
                    type="date"
                    value={formData.check_out}
                    onChange={(e) => setFormData({...formData, check_out: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Adults *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.adults}
                    onChange={(e) => setFormData({...formData, adults: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div>
                  <Label>Children</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.children}
                    onChange={(e) => setFormData({...formData, children: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Room Rate ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.base_amount}
                    onChange={(e) => setFormData({...formData, base_amount: parseFloat(e.target.value) || 0})}
                    required
                  />
                </div>
                <div>
                  <Label>Tax ($) {property && <span className="text-xs text-slate-500">(Auto-calculated)</span>}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.tax_amount}
                    readOnly={!!property}
                    onChange={(e) => !property && setFormData({...formData, tax_amount: parseFloat(e.target.value) || 0})}
                    className={property ? 'bg-slate-100' : ''}
                  />
                  {property && property.tax_total_rate > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Total Tax Rate: {property.tax_total_rate}%
                    </p>
                  )}
                </div>
              </div>
              <div className="bg-slate-50 p-3 rounded">
                <p className="text-sm font-medium">Total: ${((parseFloat(formData.base_amount) || 0) + (parseFloat(formData.tax_amount) || 0)).toFixed(2)}</p>
              </div>
            </div>
          )}

          {/* Step 2: Guest Details */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Guest Details</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowScannerModal(true)}
                  className="gap-2"
                >
                  <Scan size={16} />
                  Scan ID
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.guest_name}
                    onChange={(e) => setFormData({...formData, guest_name: e.target.value})}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.guest_email}
                    onChange={(e) => setFormData({...formData, guest_email: e.target.value})}
                    placeholder="guest@email.com"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    value={formData.guest_phone}
                    onChange={(e) => setFormData({...formData, guest_phone: e.target.value})}
                    placeholder="+1 234 567 8900"
                    required
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.guest_dob}
                    onChange={(e) => setFormData({...formData, guest_dob: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Document Type</Label>
                  <select
                    value={formData.document_type}
                    onChange={(e) => setFormData({...formData, document_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select...</option>
                    <option value="DRIVERS_LICENSE">Driver's License</option>
                    <option value="PASSPORT">Passport</option>
                    <option value="NATIONAL_ID">National ID</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.guest_address}
                    onChange={(e) => setFormData({...formData, guest_address: e.target.value})}
                    placeholder="123 Main St, City, State"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Document Number</Label>
                  <Input
                    value={formData.document_number}
                    onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                    placeholder="DL123456 or P1234567"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Internal Guest Notes (optional)</Label>
                  <textarea
                    value={formData.guest_note}
                    onChange={(e) => setFormData({...formData, guest_note: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                    rows="3"
                    placeholder="e.g., Prefers high floor, VIP client, quiet room requested..."
                  />
                  <p className="text-xs text-slate-500 mt-1">Only visible to staff</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Payment & Final Notes</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Method *</Label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Direct Billing">Direct Billing</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Payment Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.payment_amount}
                    onChange={(e) => setFormData({...formData, payment_amount: parseFloat(e.target.value)})}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Additional Notes</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                    rows="3"
                    placeholder="Special requests, booking notes..."
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold">Reservation Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Guest: {formData.guest_name}</div>
                  <div>Phone: {formData.guest_phone}</div>
                  <div>Check-in: {formData.check_in}</div>
                  <div>Check-out: {formData.check_out}</div>
                  <div>Guests: {formData.adults} adults, {formData.children} children</div>
                  <div>Rate: ${formData.base_amount}/night</div>
                  <div>Tax: ${formData.tax_amount}</div>
                  <div className="font-semibold">Total: ${(formData.base_amount + formData.tax_amount).toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </Button>

          <div className="text-sm text-slate-500">
            Step {currentStep} of 3
          </div>

          {currentStep < 3 ? (
            <Button onClick={nextStep}>
              Next
              <ChevronRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700">
              <Check size={16} className="mr-1" />
              Create Reservation
            </Button>
          )}
        </div>
      </DialogContent>
      
      {/* ID Scanner Modal */}
      <IDScannerModal
        open={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onDataScanned={handleScannedData}
      />
    </Dialog>
  );
};

export default MultiStepReservationModal;
